import { ConfirmationDialogService } from 'src/app/services/confirmation-dialog.service';
import { Component, Input, OnInit, OnChanges, inject, OnDestroy, Output, EventEmitter } from '@angular/core';
import { AutoUnsubscribe } from 'ngx-auto-unsubscribe';
import { Subscription } from 'rxjs/internal/Subscription';
import { ApiConstants } from 'src/app/base/api-constants';
import { HttpBaseService } from 'src/app/base/http-base.service';
import { AppGlobalService } from 'src/app/services/app.global.service';
import { PreValidationTblViewComponent } from '../pre-validation-tbl-view/pre-validation-tbl-view.component';
import { DialogService } from 'primeng/dynamicdialog';

import { TranslateService } from '@ngx-translate/core';
import { GlobalConstants } from 'src/app/base/global.constants';

@Component({
  selector: 'vb-schema-properties-pane',
  templateUrl: './schema-properties-pane.component.html',
  styleUrls: ['./schema-properties-pane.component.scss'],
  standalone: false  //NOSONAR
})
@AutoUnsubscribe({ arrayName: 'subscriptions' })
export class SchemaPropertiesPaneComponent implements OnInit, OnChanges, OnDestroy {
  private confirmationDialogService = inject(ConfirmationDialogService);
  private baseService = inject(HttpBaseService);
  private appGlobalService = inject(AppGlobalService);
  private dialogService = inject(DialogService);
  private translate = inject(TranslateService);

  indices = [];
  foreignKeys = [];
  @Input() selectedPropertyTitle: any;
  @Input() selectedNodeType: any;
  @Input() tableData: any;
  @Input() fieldIdMap: any;
  @Input() tableMap: any;
  @Input() uploadId;
  @Input() disallowEdit;
  @Input() tabId;
  @Input() isGenerationWizard = false;
  @Input() tableAndFieldDataMap: any = {};
  @Input() workflowAssociated = false;

  @Output() requestParentTableUpdate = new EventEmitter<any>();

  showLoader = false;
  searchTerm: any;
  subscriptions: Subscription[] = [];
  parentTableSuggestions: any = null;
  @Input() availableTables: any = [];
  selectedParentTable: any;
  oldParentTable: any = null;
  parentTableTooltipInfo = 'PARENT_TABLE_INFO';

  allowedValuesInfoBubble = 'ALLOWED_VALUES_INFO';
  allowedValuesModel: any = null;
  oldAllowedValuesModel: any = null;
  isAllowedValuesEnabled = false;
  allowedValuesFieldTypes = ['Dropdown']; 
	
	clearFilter(): void {
    this.searchTerm = '';
  }

  addToSubscriptionsList(subName: Subscription): void {
    this.subscriptions.push(subName);
  }

  /**
   * Removes a row from the table based on the specified type and data.
   *
   * @param type - The type of row to remove. Can be 'index' or 'fk'.
   * @param data - The data associated with the row to be removed.
   *
   * The function performs the following steps:
   * 1. Sets up the appropriate API constants, message, and payload based on the type.
   * 2. Displays a confirmation dialog to the user.
   * 3. If the user accepts, it makes an API call to delete the row.
   * 4. Handles the response from the API call:
   *    - If there are errors and warnings, it opens a pre-validation modal.
   *    - If the deletion is successful, it updates the local indices or foreign keys list.
   * 5. Manages the loader visibility and error handling.
   */
  removeRow(type, data: any): void {
    let msg = '';
    let apiConstants;
    let payload;
    if (type === 'index') {
      apiConstants = ApiConstants.deleteAiIndex;
      msg = `${this.translate.instant('DEL_IND_CON')} ${data.indexName}?`;
      payload = {
        orgId: this.appGlobalService.get('orgId'),
        appId: this.appGlobalService.get('appId'),
        uploadId: this.uploadId,
        tableId: this.tableData.sid,
        uniqueKeyId: data.sid
      };
    } else if (type === 'fk') {
      msg = `${this.translate.instant('DEL_FK_CON')} ${data.fkName}?`;
      apiConstants = ApiConstants.deleteAiForeignkey;
      payload = {
        orgId: this.appGlobalService.get('orgId'),
        appId: this.appGlobalService.get('appId'),
        uploadId: this.uploadId,
        tableId: this.tableData.sid,
        foreignKeyId: data.sid
      };
    }
    this.confirmationDialogService.confirm({
      header: this.translate.instant('CONFIRMATION'),
      message: msg,
      accept: () => {
        this.showLoader = true;
        const delIndSub = this.baseService[apiConstants.method](apiConstants, payload).subscribe(
          (response) => {
            this.showLoader = false;
            if (type === 'index') {
              if (response?.body) {
                if (
                  Object.prototype.hasOwnProperty.call(response.body, 'errorsAndWarnings') &&
                  response.body.errorsAndWarnings?.length > 0
                ) {
                  const err = response.body;
                  const preValmodal = this.dialogService.open(PreValidationTblViewComponent, {
                    showHeader: false,
                    styleClass: 'reference-tree',
                    modal: true,
                    data: {
                      prevalidationList: {
                        preValicationList: err.errorsAndWarnings,
                        type: 'cudConfig',
                        aiMetaData: { ...this.tableMap, ...this.fieldIdMap }
                      }
                    }
                  });
                  const sub = preValmodal.onClose.subscribe((_data: any) => {
                    /* empty*/
                  });
                  this.subscriptions.push(sub);
                } else if (type === 'index') {
                  this.indices = this.indices.filter((index: any) => {
                    return index.sid !== data.sid;
                  });
                }
              }
            } else if (type === 'fk') {
              this.foreignKeys = this.foreignKeys.filter((fk: any) => {
                return fk.sid !== data.sid;
              });
            }
          },
          (e) => {
            console.error(e);
            this.showLoader = false;
          }
        );
        this.addToSubscriptionsList(delIndSub);
      },
      reject: () => {
        return;
      }
    });
  }

  formIndexAndFK() {
    // Only process for TABLE nodes - fields don't have indexes/foreign keys
    if (this.selectedNodeType !== 'TABLE' || !this.tableData) {
      this.indices = [];
      this.foreignKeys = [];
      return;
    }
    try {
      const indexes = this.tableData.t_index ? JSON.parse(this.tableData.t_index) : [];
      this.indices = indexes.map((index) => {
        const fieldNames: any = [];
        let fieldIds = index.fields;
        if (typeof fieldIds === 'string') {
          fieldIds = fieldIds.split(',');
        }
        fieldIds.forEach((e) => {
          const fieldName = this.fieldIdMap?.[e];
          if (fieldName) {
            fieldNames.push(fieldName);
          }
        });
        return {
          ...index,
          ...{
            fieldnames: fieldNames.join(', ')
          }
        };
      });
      const fks = this.tableData.t_fKey ? JSON.parse(this.tableData.t_fKey) : [];
      this.foreignKeys = fks.map((fk) => {
        return {
          ...fk,
          ...{
            tableName: this.tableMap[fk.parentTableSid],
            indexName: this.tableMap[fk.parentIndexId]
          }
        };
      });
    } catch (error) {
      console.log(error);
    }
  }

  search(event) {

    let filtered: any[] = [];
    const query = event.query;
    if (!this.availableTables?.length) {
      this.parentTableSuggestions = [];
      return;
    }
    for (const tableName of this.availableTables) {
      if (tableName?.name.toLowerCase().indexOf(query.toLowerCase()) >= 0) {
        filtered.push(tableName);
      }
    }
    // Exclude the current table from suggestions
    const currentTableName = this.tableData?.name;
    if (currentTableName) {
      filtered = filtered.filter((o) => o.name !== currentTableName);
    }
    this.parentTableSuggestions = filtered;

  }


  ngOnChanges() {
    this.formIndexAndFK();
    this.initParentTableValue();
    this.initAllowedValues();
  }

  ngOnInit() {
    this.formIndexAndFK();
    this.initParentTableValue();
    this.initAllowedValues();
    if(this.isGenerationWizard && this.workflowAssociated) {
      this.allowedValuesInfoBubble = `${this.translate.instant('ALLOWED_VALUES_INFO')} \n\n ${this.translate.instant('WORKFLOW_ASSOCIATED_ALLOWED_VALUE_TOOLTIP')}`;
    }
  }

  /**
   * Initialize parent table value from tableData
   * Only applicable for TABLE nodes
   */
  initParentTableValue() {
    if (this.selectedNodeType !== 'TABLE') {
      this.selectedParentTable = null;
      this.oldParentTable = null;
      return;
    }
    if (this.tableData?.t_pTable && this.tableData?.t_pTableId) {
      this.selectedParentTable = {
        name: this.tableData.t_pTable,
        sid: this.tableData.t_pTableId
      };
      this.oldParentTable = this.selectedParentTable;
    } else {
      this.selectedParentTable = null;
      this.oldParentTable = null;
    }
  }

  /**
   * Called when user selects a parent table from autocomplete.
   * Emits CREATE or UPDATE event to parent component.
   */
  onParentTableSelect(event: any) {
    const selectedTable = event.value;
    if (!selectedTable || !selectedTable.sid) {
      return;
    }
    const action = this.oldParentTable ? GlobalConstants.update : GlobalConstants.create;

    this.requestParentTableUpdate.emit({
      action: action,
      selectedTable: selectedTable,
      tableData: this.tableData,
      oldParentTable: this.oldParentTable
    });

    // Update local state optimistically
    this.oldParentTable = selectedTable;
    this.tableData.t_pTable = selectedTable.name;
    this.tableData.t_pTableId = selectedTable.sid;
  }

  /**
   * Called when user clears the parent table.
   * Shows confirmation and emits DELETE event to parent component.
   */
  onClearParentTable() {
    if (this.disallowEdit || !this.selectedParentTable) {
      return;
    }

    this.confirmationDialogService.confirm({
      message: this.translate.instant('CONFIRM_DELETE_PARENT_TABLE') || 'Are you sure you want to remove the parent table? This will delete the Pid field.',
      accept: () => {
        let pidSid: any = null;
        if (this.tableAndFieldDataMap) {
          this.tableAndFieldDataMap[this.tableData.sid]?.fields.forEach((field) => {
            if(field.name === 'Pid') {
              pidSid = field.sid;
            }
          });
        }

        this.requestParentTableUpdate.emit({
          action: GlobalConstants.delete,
          selectedTable: null,
          tableData: this.tableData,
          oldParentTable: this.oldParentTable,
          pidSid: pidSid
        });
      },
      reject: () => this.selectedParentTable = this.oldParentTable
    });
  }

  initAllowedValues() {
    if (this.selectedNodeType !== 'FIELD' || !this.tableData) {
      this.allowedValuesModel = null;
      this.oldAllowedValuesModel = null;
      this.isAllowedValuesEnabled = false;
      return;
    }

    const fieldType = this.tableData.f_type;
    this.isAllowedValuesEnabled = this.allowedValuesFieldTypes.includes(fieldType);
    this.allowedValuesModel = this.tableData.f_aValues || null;
    this.oldAllowedValuesModel = this.allowedValuesModel;
  }

  onAllowedValuesModelChange(allowedValues: string) {
    if (!this.isAllowedValuesEnabled || !this.tableData) {
      return;
    }

    this.showLoader = true;
    
    const payload = {
      orgId: this.appGlobalService.get('orgId'),
      aid: this.appGlobalService.get('appId'),
      sid: this.tableData.sid,
      tabId: this.tabId,
      f_tableId: this.tableData.f_tableId,
      type: 'FIELD',
      old_f_aValues: this.tableData.f_aValues || undefined,
      f_type: this.tableData.f_type,
      f_aValues: allowedValues
    };

    const updateSub = this.baseService.put(ApiConstants.updateAiAllowedValues, payload).subscribe({
      next: () => {
        this.showLoader = false;
        this.tableData.f_aValues = allowedValues;
        this.oldAllowedValuesModel = allowedValues;
      },
      error: () => {
        this.showLoader = false;
        this.allowedValuesModel = this.oldAllowedValuesModel;
      }
    });
    this.addToSubscriptionsList(updateSub);
  }

  ngOnDestroy(): void {
    /* required for AutoUnsubscribe */
  }

}



